import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Package2, Clock, X, Smartphone, Shirt, Sofa, Home as HomeIcon, Car, BookOpen, Dumbbell, UtensilsCrossed, Leaf, Wrench, Sparkles, Ticket, Mic, MicOff, Globe, Package, Gift, Music, Gamepad2, Camera, Watch, LayoutGrid } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '@/App';
import AdBanner from '@/components/AdBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Lookup map for dynamic icon names returned from backend
const ICON_MAP = {
  Smartphone, Shirt, Sofa, Home: HomeIcon, Car, BookOpen, Dumbbell,
  UtensilsCrossed, Leaf, Wrench, Sparkles, Ticket, Package, Gift,
  Music, Gamepad2, Camera, Watch,
};

const fallbackCategoryData = [
  { name: 'Electronics', icon_name: 'Smartphone', image_url: 'https://static.prod-images.emergentagent.com/jobs/4114543f-bbee-4cf6-9d8b-f005d9297ec7/images/be9056ebd8c02c3ba053e29a5a87d8492ca3df1bff8734ce04c0cd94e3dbbb25.png', gradient: '#3B82F6, #06B6D4' },
  { name: 'Fashion', icon_name: 'Shirt', image_url: 'https://static.prod-images.emergentagent.com/jobs/4114543f-bbee-4cf6-9d8b-f005d9297ec7/images/16f5540e37ae230a48dca819f366571a8d4a97a452befb370c9a685eaa081e14.png', gradient: '#EC4899, #F43F5E' },
  { name: 'Other', icon_name: 'Sparkles', image_url: '', gradient: '#64748B, #475569' },
];

// 3D Icon Component
const Icon3D = ({ Icon, gradient }) => (
  <div 
    className="w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg transform-gpu"
    style={{ 
      background: `linear-gradient(135deg, ${gradient})`,
      boxShadow: '0 8px 16px -4px rgba(0,0,0,0.2), 0 4px 8px -2px rgba(0,0,0,0.1), inset 0 2px 4px rgba(255,255,255,0.3)'
    }}
  >
    <Icon className="w-5 h-5 md:w-7 md:h-7 text-white drop-shadow-md" strokeWidth={2} />
  </div>
);

export default function HomePage() {
  const [listings, setListings] = useState([]);
  const [categoryData, setCategoryData] = useState(fallbackCategoryData);
  const [themeSettings, setThemeSettings] = useState({
    theme_primary_color: '#4F46E5',
    theme_accent_color: '#10B981',
    theme_hero_gradient_from: '#0F172A',
    theme_hero_gradient_via: '#312E81',
    theme_hero_gradient_to: '#4C1D95',
  });
  const [search, setSearch] = useState('');
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Sync category state with ?category=X URL query (set when user clicks a chip from another page)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cat = params.get('category') || '';
    setCategory(cat);
    // Scroll to top so the user immediately sees the category banner + filtered listings
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [location.search]);

  useEffect(() => {
    fetchCategories();
    fetchThemeSettings();
  }, []);

  useEffect(() => {
    fetchListings();
  }, [category, search]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/categories`);
      if (Array.isArray(res.data) && res.data.length > 0) {
        setCategoryData(res.data);
      }
    } catch (err) {
      console.warn('Failed to load categories, using fallback:', err);
    }
  };

  const fetchThemeSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings/public`);
      setThemeSettings((p) => ({ ...p, ...res.data }));
    } catch (err) {
      console.warn('Failed to load theme settings, using defaults:', err);
    }
  };

  const fetchListings = async () => {
    try {
      const params = {};
      if (category) params.category = category;
      if (search) params.search = search;
      params.status = 'live';

      const response = await axios.get(`${API_URL}/api/listings`, { params });
      setListings(response.data);
    } catch (error) {
      console.error('Failed to fetch listings', error);
    } finally {
      setLoading(false);
    }
  };

  const getTimeRemaining = (endTime) => {
    const end = new Date(endTime);
    const now = new Date();
    const diff = end - now;

    if (diff <= 0) return 'Ended';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) return `${days}d ${hours}h left`;
    return `${hours}h left`;
  };

  const toggleVoiceSearch = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Voice search is not supported in your browser. Try Chrome or Edge.');
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map(result => result[0].transcript)
        .join('');
      setSearch(transcript);
    };

    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  };

  return (
    <div className="min-h-screen">
      {/* Top Ad Banner */}
      <AdBanner placement="home_top" className="max-w-7xl mx-auto px-4 pt-4" />

      {/* Hero Section - only when no category filter is active */}
      {!category && (
      <div className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${themeSettings.theme_hero_gradient_from} 0%, #1E1B4B 30%, ${themeSettings.theme_hero_gradient_via} 50%, ${themeSettings.theme_hero_gradient_to} 70%, ${themeSettings.theme_hero_gradient_from} 100%)` }}>
        {/* Animated floating culture elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {['🇮🇳','🇺🇸','🇬🇧','🇯🇵','🇧🇷','🇦🇪','🇫🇷','🇩🇪','🇰🇷','🇦🇺','🇨🇦','🇿🇦','🇸🇬','🇲🇽','🇮🇹','🇪🇸'].map((flag, i) => (
            <motion.div
              key={flag}
              className="absolute text-2xl md:text-3xl opacity-20"
              initial={{ y: '110vh', x: `${(i * 6.25)}%` }}
              animate={{ y: '-10vh' }}
              transition={{ duration: 12 + (i * 1.5), repeat: Infinity, ease: 'linear', delay: i * 0.8 }}
              style={{ left: `${(i * 6.25) + 1}%` }}
            >
              {flag}
            </motion.div>
          ))}
          {/* Glowing orbs */}
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
          <div className="absolute top-1/2 right-1/3 w-48 h-48 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8 md:py-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center"
          >
            {/* Global badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-3 py-1.5 mb-4"
            >
              <Globe className="w-3.5 h-3.5 text-cyan-400" />
              <span className="text-xs font-medium text-cyan-100">Trusted by traders across 50+ countries</span>
            </motion.div>

            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-white mb-3 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Trade What You Have
              <span className="hidden sm:inline">&nbsp;</span>
              <br className="sm:hidden" />
              <span className="bg-gradient-to-r from-cyan-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">
                For What You Want
              </span>
            </h1>
            <p className="text-sm md:text-base text-indigo-200/90 mb-5 max-w-2xl mx-auto px-4 leading-relaxed">
              The global marketplace for barter, trade, and sustainable commerce.
            </p>

            {/* Hero CTA buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/create-listing"
                className="inline-flex items-center gap-2 rounded-full font-bold px-6 py-2.5 bg-gradient-to-r from-lime-400 to-emerald-500 text-slate-900 hover:from-lime-300 hover:to-emerald-400 shadow-lg shadow-lime-500/25 hover:shadow-xl hover:shadow-lime-500/30 transition-all text-sm"
                data-testid="hero-create-listing-button"
              >
                Start Trading Now
              </Link>
              <Link
                to="/faq"
                className="inline-flex items-center gap-2 rounded-full font-semibold px-5 py-2 bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 transition-all text-sm"
              >
                How it Works
              </Link>
            </div>

            {/* Stats row */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="mt-6 flex items-center justify-center gap-6 md:gap-12"
            >
              {[
                { value: '50+', label: 'Countries' },
                { value: '12', label: 'Categories' },
                { value: '24/7', label: 'Trading' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <p className="text-lg md:text-xl font-black text-white">{stat.value}</p>
                  <p className="text-[10px] md:text-xs text-indigo-300">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
      )}

      {/* Categories Section */}
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
        {!category && (
        <div className="mb-6 md:mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Browse by Category
          </h2>
          <p className="text-slate-600 text-sm md:text-base">Find what you're looking for</p>
        </div>
        )}

        {/* Category Banner (only when a category is active) */}
        {category && (
          <div
            className="mb-6 md:mb-8 rounded-2xl p-5 md:p-6 flex items-center justify-between text-white shadow-lg"
            style={(() => {
              const cat = categoryData.find((c) => c.name === category);
              return { background: `linear-gradient(135deg, ${cat?.gradient || '#4F46E5, #312E81'})` };
            })()}
            data-testid="active-category-banner"
          >
            <div className="flex items-center gap-3">
              {(() => {
                const cat = categoryData.find((c) => c.name === category);
                const IconComp = cat?.Icon || ICON_MAP[cat?.icon_name] || Sparkles;
                return (
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center">
                    <IconComp className="w-5 h-5 md:w-6 md:h-6 text-white" />
                  </div>
                );
              })()}
              <div>
                <h2 className="text-xl md:text-2xl font-bold leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{category}</h2>
                <p className="text-xs md:text-sm text-white/85">
                  {loading ? 'Loading…' : `${listings.length} listing${listings.length === 1 ? '' : 's'} available`}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-white text-xs md:text-sm font-medium backdrop-blur-sm transition"
              data-testid="clear-category-filter-btn"
            >
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          </div>
        )}

        {/* Category Cards Grid - hide when a category is already selected */}
        {!category && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-6 gap-3 md:gap-4 mb-6 md:mb-8">
          {categoryData.map((cat) => (
            <motion.button
              key={cat.name}
              onClick={() => {
                const newCategory = category === cat.name ? '' : cat.name;
                setCategory(newCategory);
                if (newCategory) {
                  setTimeout(() => {
                    window.scrollTo({ top: 800, behavior: 'smooth' });
                  }, 100);
                }
              }}
              whileHover={{ y: -6, scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              className={`relative overflow-hidden rounded-2xl transition-all duration-300 cursor-pointer flex flex-col items-center ${
                category === cat.name
                  ? 'ring-3 ring-indigo-600 shadow-xl shadow-indigo-200'
                  : 'hover:shadow-lg border border-slate-200 bg-white'
              }`}
              data-testid={`category-card-${cat.name}`}
            >
              {/* 3D Image */}
              <div className="w-full aspect-square overflow-hidden rounded-t-2xl" style={{
                background: `linear-gradient(135deg, ${cat.gradient})`
              }}>
                <img
                  src={cat.image_url || cat.image}
                  alt={cat.name}
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                  loading="lazy"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
              {/* Label */}
              <div className={`w-full py-2 px-1 text-center`}
                   style={category === cat.name ? { background: themeSettings.theme_primary_color } : { background: 'white' }}>
                <h3 className={`font-bold text-[10px] md:text-xs leading-tight ${
                  category === cat.name ? 'text-white' : 'text-slate-900'
                }`}>
                  {cat.name}
                </h3>
              </div>
            </motion.button>
          ))}
        </div>
        )}

        {/* Search and Filters Below Categories */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-lg border border-slate-200 p-4 md:p-6 mb-6 md:mb-8" style={{ zIndex: 5 }}>
          <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-1 md:mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Search All Listings
          </h3>
          <p className="text-xs md:text-sm text-slate-500 mb-3 md:mb-4">Search by title, description, city, state, country, or pin code</p>
          <div className="flex flex-col md:flex-row gap-3 md:gap-4 items-stretch md:items-center">
            <div className="flex-1 relative w-full">
              <Search className="absolute left-3 md:left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 md:w-5 h-4 md:h-5 pointer-events-none" />
              <input
                type="text"
                placeholder="Search by name, city, pin code..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 md:pl-12 pr-12 rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all h-11 md:h-12 px-4 text-sm md:text-base"
                data-testid="search-input"
              />
              {/* Voice Search Button */}
              <button
                onClick={toggleVoiceSearch}
                data-testid="voice-search-btn"
                className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                  isListening
                    ? 'bg-red-500 text-white animate-pulse shadow-lg shadow-red-500/40'
                    : 'bg-slate-100 text-slate-500 hover:bg-indigo-100 hover:text-indigo-600'
                }`}
                title={isListening ? 'Stop listening' : 'Voice search'}
              >
                {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
            </div>
            {(category || search) && (
              <button
                onClick={() => {
                  setCategory('');
                  setSearch('');
                }}
                className="px-4 md:px-6 py-2 md:py-3 bg-slate-100 hover:bg-slate-200 rounded-xl font-medium text-slate-700 transition-colors whitespace-nowrap text-sm md:text-base"
                data-testid="clear-filters-button"
              >
                Clear Filters
              </button>
            )}
          </div>

          {/* Active Filters Display */}
          {(category || search) && (
            <div className="flex flex-wrap gap-2 mt-3 md:mt-4">
              {category && (
                <div className="inline-flex items-center space-x-1 md:space-x-2 bg-indigo-100 text-indigo-700 px-3 md:px-4 py-1.5 md:py-2 rounded-full font-semibold text-xs md:text-sm">
                  {(() => {
                    const catData = categoryData.find(c => c.name === category);
                    const IconComp = catData && (catData.Icon || ICON_MAP[catData.icon_name] || Sparkles);
                    return IconComp ? <IconComp className="w-3 h-3 md:w-4 md:h-4" /> : null;
                  })()}
                  <span>{category}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCategory('');
                    }}
                    className="ml-1 hover:bg-indigo-200 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3 md:w-4 md:h-4" />
                  </button>
                </div>
              )}
              {search && (
                <div className="inline-flex items-center space-x-1 md:space-x-2 bg-lime-100 text-lime-700 px-3 md:px-4 py-1.5 md:py-2 rounded-full font-semibold text-xs md:text-sm">
                  <Search className="w-3 h-3 md:w-4 md:h-4" />
                  <span>"{search}"</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearch('');
                    }}
                    className="ml-1 hover:bg-lime-200 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3 md:w-4 md:h-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Middle Ad Banner - Between Categories and Listings */}
        <AdBanner placement="home_middle" className="mb-6 md:mb-8" />

        {/* Search Results Top Ad */}
        <AdBanner placement="search_top" className="mb-4 md:mb-6" />

        <div data-testid="listings-grid-anchor" />

        {/* Listings Grid */}
        {loading ? (
          <div className="text-center py-12 md:py-20">
            <div className="animate-spin rounded-full h-12 md:h-16 w-12 md:w-16 border-b-2 border-indigo-600 mx-auto"></div>
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-12 md:py-20">
            <Package2 className="w-16 md:w-20 h-16 md:h-20 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg md:text-xl font-semibold text-slate-900 mb-2">No listings found</h3>
            <p className="text-slate-600 text-sm md:text-base">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
            {listings.map((listing) => (
              <motion.div
                key={listing.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ y: -4 }}
                transition={{ duration: 0.2 }}
              >
                <Link
                  to={`/listing/${listing.id}`}
                  className="group relative bg-white rounded-xl md:rounded-2xl overflow-hidden border border-slate-100 hover:shadow-xl hover:shadow-indigo-500/10 transition-all duration-300 block"
                  data-testid={`listing-card-${listing.id}`}
                >
                  <div className="aspect-square overflow-hidden bg-slate-100 relative">
                    {listing.images && listing.images.length > 0 ? (
                      <img
                        src={listing.images[0]}
                        alt={listing.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package2 className="w-12 md:w-20 h-12 md:h-20 text-slate-300" />
                      </div>
                    )}
                    {/* Your Listing Badge */}
                    {user && listing.user_id === user.id && (
                      <div className="absolute top-2 left-2 md:top-3 md:left-3 bg-indigo-600 text-white text-[10px] md:text-xs font-bold px-2 md:px-3 py-0.5 md:py-1 rounded-full shadow-lg">
                        Your Listing
                      </div>
                    )}
                  </div>
                  <div className="p-3 md:p-4">
                    <div className="flex items-start justify-between mb-1 md:mb-2 gap-2">
                      <h3 className="font-semibold text-slate-900 text-sm md:text-lg line-clamp-1">{listing.title}</h3>
                      <span className="text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 md:py-1 bg-indigo-100 text-indigo-700 rounded-full font-medium whitespace-nowrap">
                        {listing.category}
                      </span>
                    </div>
                    <p className="text-slate-600 text-xs md:text-sm line-clamp-2 mb-2 md:mb-3">{listing.description}</p>
                    <div className="flex items-center justify-between text-xs md:text-sm">
                      <span className="text-slate-500 flex items-center">
                        <Clock className="w-3 md:w-4 h-3 md:h-4 mr-1" />
                        {getTimeRemaining(listing.bidding_end_time)}
                      </span>
                      <span className="font-semibold text-indigo-600">{listing.bids_count} bids</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        {/* Bottom Ad Banner */}
        <AdBanner placement="home_bottom" className="mt-8 md:mt-12" />
      </div>
    </div>
  );
}
